import { Building2, Globe2, Undo2 } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export interface ScopeBannerCopy {
  generalTitle: string;
  generalBody: string;
  generalOrganizations: (count: number) => string;
  organizationTitle: (name: string) => string;
  organizationBody: (name: string) => string;
  backToGeneral: string;
}

export interface ScopeBannerProps {
  organizationName: string | null;
  /** General scope only. */
  specialPriceOrganizations: number;
  onBackToGeneral: () => void;
  /** Defaults to the price book's wording. */
  copy?: ScopeBannerCopy;
}

/** Says, in words, whose prices are on screen and who a change made here reaches. */
export function ScopeBanner({
  organizationName,
  specialPriceOrganizations,
  onBackToGeneral,
  copy,
}: ScopeBannerProps) {
  const { t } = useTranslations("paymentFees");
  const text: ScopeBannerCopy = copy ?? {
    generalTitle: t("banner_general_title"),
    generalBody: t("banner_general_body"),
    generalOrganizations: (count) => t("banner_general_orgs", { count }),
    organizationTitle: (name) => t("banner_org_title", { name }),
    organizationBody: (name) => t("banner_org_body", { name }),
    backToGeneral: t("banner_back_to_general"),
  };
  const isSpecial = organizationName != null;
  const Icon = isSpecial ? Building2 : Globe2;

  return (
    <section
      aria-live="polite"
      className={cn(
        "flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center",
        isSpecial ? "border-primary/40 bg-primary/5" : "border-border bg-muted/50",
      )}
    >
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-lg",
          isSpecial ? "bg-primary/15 text-primary" : "bg-background text-foreground",
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-semibold">
          {isSpecial ? text.organizationTitle(organizationName) : text.generalTitle}
        </p>
        <p className="text-sm text-muted-foreground">
          {isSpecial ? text.organizationBody(organizationName) : text.generalBody}
        </p>
        {!isSpecial && specialPriceOrganizations > 0 && (
          <p className="text-xs text-muted-foreground">
            {text.generalOrganizations(specialPriceOrganizations)}
          </p>
        )}
      </div>
      {isSpecial && (
        <ColocaButton
          variant="default"
          size="sm"
          icon={Undo2}
          onClick={onBackToGeneral}
          className="self-start"
        >
          {text.backToGeneral}
        </ColocaButton>
      )}
    </section>
  );
}
